// M141 — /effect — 4-wekelijkse cyclus-resultaten en delta's per pagina.
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useEmbed } from "@/contexts/EmbedContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, TrendingUp, TrendingDown, Minus, LineChart, ArrowRight } from "lucide-react";

function DeltaPill({ delta }) {
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-pos-green/40 text-pos-green bg-pos-green/10">
        <TrendingUp className="w-3 h-3" /> +{delta}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-neg-red/40 text-neg-red bg-neg-red/10">
        <TrendingDown className="w-3 h-3" /> {delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-border-hairline text-text-muted">
      <Minus className="w-3 h-3" /> geen verandering
    </span>
  );
}

function fmtRange(a, b) {
  if (!a || !b) return "";
  const da = new Date(a).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" });
  const db = new Date(b).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" });
  return `${da} – ${db}`;
}

export default function EffectPage() {
  const { hideInnerHeader } = useEmbed();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        const [cy, cur] = await Promise.all([
          api.get("/effect/cycles"),
          api.get("/effect/current"),
        ]);
        setData(cy.data);
        setCurrent(cur.data);
      } catch (e) {
        setErr("Kon resultaten niet ophalen.");
      }
    })();
  }, []);

  const completed = (data?.cycles || []).filter((c) => c.status === "completed");
  const activeCycle = current;
  const daysLeft = activeCycle?.ends_at
    ? Math.max(0, Math.ceil((new Date(activeCycle.ends_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <AppLayout>
      {!hideInnerHeader && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary tracking-tight flex items-center gap-2"
               data-testid="effect-title">
            <LineChart className="w-5 h-5 text-accent-teal" /> Resultaten per cyclus
          </h1>
          <p className="text-sm text-text-muted mt-1">
            4-wekelijkse metingen — score, Google-positie en bezoekers.
          </p>
        </div>
      )}

      {err && <div className="text-xs text-warn-amber italic mb-4">{err}</div>}
      {!data && <div className="text-text-muted text-sm flex items-center gap-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cyclussen laden…
      </div>}

      {activeCycle && (
        <div data-testid="effect-active-cycle"
              className="rounded-card border border-accent-teal/40 bg-accent-teal/5 p-4 mb-5">
          <div className="text-[10px] text-accent-teal">Huidige cyclus</div>
          <div className="text-sm text-text-primary mt-1">
            Loopt van <span className="font-semibold">{fmtRange(activeCycle.started_at, activeCycle.ends_at)}</span>.
            {daysLeft !== null && (daysLeft > 0
              ? ` Eerste meting over ${daysLeft} ${daysLeft === 1 ? "dag" : "dagen"}.`
              : " Cyclus is klaar om gesloten te worden.")}
          </div>
        </div>
      )}

      {completed.length === 0 ? (
        <div data-testid="effect-no-cycles"
              className="rounded-card border border-dashed border-border-hairline bg-bg-panel p-8 text-center">
          <LineChart className="w-8 h-8 text-text-muted mx-auto mb-3" />
          <div className="text-sm font-display text-text-primary mb-2">
            Eerste resultaten na 4 weken
          </div>
          <p className="text-xs text-text-muted max-w-md mx-auto mb-4 leading-relaxed">
            Zodra de huidige cyclus klaar is meet JustEasySEO de verschillen per pagina.
            Terwijl je wacht: pak adviezen op en markeer ze als "gedaan".
          </p>
          <button onClick={() => nav("/adviezen")} data-testid="effect-goto-adviezen"
                   className="text-[11px] px-3 py-1.5 rounded border border-accent-teal/40 text-accent-teal hover:bg-accent-teal/10 inline-flex items-center gap-1">
            Naar adviezen <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div className="space-y-6" data-testid="effect-cycles-list">
          {completed.map((c, ci) => (
            <div key={c.cycle_id} data-testid={`effect-cycle-${ci}`}
                  className="rounded-card border border-border-hairline bg-bg-panel p-4">
              <div className="flex items-baseline justify-between gap-2 mb-3">
                <div>
                  <div className="text-lg font-display text-text-primary">
                    Cyclus {completed.length - ci} ({fmtRange(c.started_at, c.completed_at)})
                  </div>
                  <div className="text-xs text-text-muted mt-1">
                    <span className="text-pos-green font-semibold">{c.improved_count}</span> van de {c.total_pages} pagina's verbeterd
                  </div>
                </div>
              </div>
              <div className="space-y-2 overflow-x-auto -mx-1 px-1">
                {(c.deltas || []).map((d, di) => (
                  <div key={d.page_id} data-testid={`effect-delta-${ci}-${di}`}
                        className="rounded border border-border-hairline bg-bg-panel-raised px-3 py-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 min-w-0">
                    <div className="min-w-0 flex-1 w-full">
                      <div className="text-sm text-text-primary truncate">{d.title || d.url}</div>
                      <div className="text-[10px] text-text-muted truncate">{d.url}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap sm:flex-nowrap">
                      <div className="text-right">
                        <div className="text-[10px] text-text-muted">Score</div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-text-muted num">{d.nexora_score_before}→{d.nexora_score_after}</span>
                          <DeltaPill delta={d.score_delta || 0} />
                        </div>
                        <div className="text-[9px] text-text-subtle italic max-w-[140px]">Punten JustEasySEO-score</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-text-muted">Google-positie</div>
                        <div className="text-[11px] text-text-primary max-w-[220px] truncate" title={d.gsc_delta_nl}>{d.gsc_delta_nl}</div>
                        <div className="text-[9px] text-text-subtle italic">Uit Search Console</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-text-muted">Bezoekers</div>
                        <div className="text-[11px] text-text-muted italic">{d.ga4_visitors_delta_nl}</div>
                        <div className="text-[9px] text-text-subtle italic">Uit Analytics</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
